import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import LivePricingTable from './widgets/LivePricingTable';
import HospitalComparisonChart from './widgets/HospitalComparisonChart';
import PriceBreakdownByDosage from './widgets/PriceBreakdownByDosage';

// Create a QueryClient instance for the widgets
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

interface EducationalContentRendererProps {
  htmlContent: string;
  procedureCode: string;
  codeType: string;
}

/**
 * Component that renders educational HTML and replaces special placeholder tags
 * with live React components that fetch current pricing data.
 *
 * Supported placeholder tags:
 * - <live-pricing-table code="..." type="..."></live-pricing-table>
 * - <hospital-comparison code="..." type="..."></hospital-comparison>
 * - <price-breakdown code="..." type="..."></price-breakdown>
 */
const EducationalContentRenderer: React.FC<EducationalContentRendererProps> = ({
  htmlContent,
  procedureCode,
  codeType
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!containerRef.current || !htmlContent) return;

    // Set the HTML content
    containerRef.current.innerHTML = htmlContent;

    // Find and replace all live-pricing-table tags
    const pricingTables = containerRef.current.querySelectorAll('live-pricing-table');
    pricingTables.forEach((element) => {
      const code = element.getAttribute('code') || procedureCode;
      const type = element.getAttribute('type') || codeType;
      const wrapper = document.createElement('div');
      element.parentNode?.replaceChild(wrapper, element);
      const root = createRoot(wrapper);
      root.render(
        <QueryClientProvider client={queryClient}>
          <LivePricingTable code={code} codeType={type} />
        </QueryClientProvider>
      );
    });

    // Find and replace all hospital-comparison tags
    const hospitalCharts = containerRef.current.querySelectorAll('hospital-comparison');
    hospitalCharts.forEach((element) => {
      const code = element.getAttribute('code') || procedureCode;
      const type = element.getAttribute('type') || codeType;
      const setting = element.getAttribute('setting') || 'all';
      const unit = element.getAttribute('unit') || 'all';
      const limit = parseInt(element.getAttribute('limit') || '20', 10);

      const wrapper = document.createElement('div');
      element.parentNode?.replaceChild(wrapper, element);
      const root = createRoot(wrapper);
      root.render(
        <QueryClientProvider client={queryClient}>
          <HospitalComparisonChart
            code={code}
            codeType={type}
            setting={setting}
            unit={unit}
            limit={limit}
          />
        </QueryClientProvider>
      );
    });

    // Find and replace all price-breakdown tags
    const priceBreakdowns = containerRef.current.querySelectorAll('price-breakdown');
    priceBreakdowns.forEach((element) => {
      const code = element.getAttribute('code') || procedureCode;
      const type = element.getAttribute('type') || codeType;

      const wrapper = document.createElement('div');
      element.parentNode?.replaceChild(wrapper, element);
      const root = createRoot(wrapper);
      root.render(
        <QueryClientProvider client={queryClient}>
          <PriceBreakdownByDosage code={code} codeType={type} />
        </QueryClientProvider>
      );
    });

    setMounted(true);
  }, [htmlContent, procedureCode, codeType]);

  return (
    <div
      ref={containerRef}
      className="prose prose-invert max-w-none educational-content"
    />
  );
};

export default EducationalContentRenderer;
